// src/components/aols/profile/AboutAA.tsx
import React from "react";
import { Layout, Typography, Button } from "antd";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Footer } from "../../dev/utils/Footer";
import styled from "styled-components";

// Define TypeScript interfaces
interface AboutAAProps {}

// Custom Breadcrumb Component
const CustomBreadcrumb = styled.div`
  margin-bottom: 20px;
  text-align: left;
  font-size: 14px;
  line-height: 1.5714285714285714;

  .breadcrumb-item {
    display: inline-block;
  }

  .breadcrumb-link {
    color: #ffaa00 !important; /* Sunlight orange for active links */
    text-decoration: none;
  }

  .breadcrumb-link:hover {
    color: #fce248 !important; /* Sunlight yellow on hover */
  }

  .breadcrumb-separator {
    color: #fff; /* White separator for contrast */
    margin: 0 8px;
  }

  .breadcrumb-non-active {
    color: #fff !important; /* White for non-active items */
  }
`;

// Ant Design components with TypeScript typing
const { Title, Paragraph } = Typography;

// Styled-components for styling
const FullHeightLayout = styled(Layout)`
  min-height: 100vh;
  background: linear-gradient(180deg, #000000 0%, #1a1a1a 100%);
`;

const CustomButton = styled(Button)`
  margin-right: 20px;
  background: #3552fc; /* Deep blue */
  color: white;
  border: none;
  padding: 5px 15px;
  border-radius: 5px;
  transition: background 0.3s ease;

  &:hover {
    background: #40a9ff; /* Lighter blue */
    color: white;
  }

  @media (max-width: 768px) {
    padding: 5px 10px;
    font-size: 14px;
  }
`;

const MainContent = styled(Layout.Content)`
  padding: 100px 20px 20px; /* Increased top padding to account for TopRightContainer */
  text-align: center;
  flex: 1;
  min-height: calc(100vh - 134px); /* Adjust for footer */

  @media (max-width: 768px) {
    padding: 120px 10px 10px; /* Adjusted for mobile */
  }
`;

const ProfileContainer = styled.div`
  background: #1a1a1a; /* Dark background */
  color: #0427f4; /* OLS blue */
  margin: 0 auto;
  max-width: 900px;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 20px;
    margin: 10px;
  }
`;

const SectionTitle = styled(Title)`
  color: #ffaa00 !important; /* Sunlight orange */
  font-size: 2.5em !important;
  margin-bottom: 20px !important;

  @media (max-width: 768px) {
    font-size: 1.8em !important;
  }
`;

const FeatureCard = styled.div`
  background: #fce248; /* Sunlight yellow */
  padding: 20px;
  border-radius: 8px;
  border: 2px solid #ffcc00;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const BioSectionTitle = styled(Title)`
  color: #3552fc !important; /* Deep blue */
  font-size: 1.5em !important;
  margin-top: 20px !important;
  margin-bottom: 10px !important;
  border-bottom: 2px solid #ffaa00;
  padding-bottom: 5px;

  @media (max-width: 768px) {
    font-size: 1.3em !important;
  }
`;

const BioText = styled(Paragraph)`
  color: #333 !important; /* Darker for readability */
  font-size: 1.1em !important;
  line-height: 1.8 !important;
  text-align: left !important;
  margin-bottom: 15px !important;

  a {
    color: #3552fc;
    text-decoration: underline;
  }

  a:hover {
    color: #40a9ff;
  }

  strong {
    color: #0427f4; /* OLS blue for emphasis */
  }

  @media (max-width: 768px) {
    font-size: 1em !important;
  }
`;

const BioList = styled.ul`
  list-style-type: disc;
  margin-left: 20px;
  margin-bottom: 15px;
  color: #333; /* Darker for readability */

  li {
    margin-bottom: 10px;
    color: #333;

    strong {
      color: #0427f4; /* OLS blue for emphasis */
    }
  }
`;

const StyledFooter = styled(Layout.Footer)`
  background: #ffcc00; /* Sunlight yellow */
  color: #333;
  text-align: center;
  padding: 15px 0;
  font-size: 14px;
  position: relative;
  bottom: 0;
  width: 100%;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);

  a {
    color: #333;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const AboutAA: React.FC<AboutAAProps> = () => {
  return (
    <FullHeightLayout>
      <Helmet>
        <title>Asvitloaten | OLS Founder & Master Teacher - onelightsystem</title>
        <meta
          name="description"
          content="Meet Asvitloaten (Nazar Pankiv), founder of onelightsystem OLS and Master Teacher L4 with 19,608 Light Minutes. Discover his journey in Sun Light Meditation and Light Civilization."
        />
        <meta
          name="keywords"
          content="Asvitloaten, Nazar Pankiv, OLS founder, Sun Light Meditation, Light Civilization, meditation teacher, OLS Meditation Academy, spiritual leader, Light Minutes"
        />
        <meta name="author" content="onelightsystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Asvitloaten | OLS Founder & Master Teacher - onelightsystem" />
        <meta
          property="og:description"
          content="Learn about Asvitloaten, aka Nazar Pankiv, the visionary behind OLS, pioneering Sun Light Meditation with 19,608 Light Minutes."
        />
        <meta property="og:url" content="https://www.olsme.com/about-aa" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="Asvitloaten, OLS Founder, teaching Sun Light Meditation" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Asvitloaten | OLS Founder & Master Teacher - onelightsystem" />
        <meta name="twitter:description" content="Learn about Asvitloaten, aka Nazar Pankiv, the visionary behind OLS, pioneering Sun Light Meditation with 19,608 Light Minutes." />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <link rel="canonical" href="https://www.olsme.com/about-aa" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.olsme.com/",
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Asvitloaten",
                "item": "https://www.olsme.com/aa",
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "About",
                "item": "https://www.olsme.com/about-aa",
              },
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Nazar Pankiv",
            "alternateName": "Asvitloaten",
            "jobTitle": "Founder and Master Teacher L4, onelightsystem OLS",
            "description": "Asvitloaten (Nazar Pankiv) founded onelightsystem OLS in 2017, pioneering Sun Light Meditation with 19,608 Light Minutes by March 26, 2025, to foster a global Light Civilization.",
            "url": "https://www.olsme.com/about-aa",
            "birthDate": "1984-10-14",
            "datePublished": "2017-04-01",
            "dateModified": "2025-03-26",
            "birthPlace": {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Ternopil",
                "addressCountry": "UA",
              },
            },
            "nationality": "Ukrainian",
            "affiliation": {
              "@type": "Organization",
              "name": "onelightsystem OLS",
              "url": "https://www.olsme.com",
            },
            "knowsAbout": [
              "Sun Light Meditation",
              "Light Civilization",
              "spiritual teaching",
              "environmental advocacy",
            ],
            "sameAs": [
              "https://www.ols-med.net/olsm/ols-eae-teache/nazarasvilto/about-nazar",
              "https://www.linkedin.com/in/nazar-pankiv",
              "https://www.udemy.com/user/nazar-10/",
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "onelightsystem OLS",
            "url": "https://www.olsme.com",
            "logo": "https://www.olsme.com/logo.jpg",
            "description": "A global platform advancing Sun Light Meditation and fostering a Light Civilization.",
            "founder": {
              "@type": "Person",
              "name": "Nazar Pankiv",
              "alternateName": "Asvitloaten",
            },
          })}
        </script>
      </Helmet>

      <MainContent>
        <ProfileContainer>
          <CustomBreadcrumb>
            <span className="breadcrumb-item">
              <Link to="/Stat" className="breadcrumb-link">
                Stat
              </Link>
            </span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-item">
              <Link to="/aa" className="breadcrumb-link">
                Asvitloaten
              </Link>
            </span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-item breadcrumb-non-active">
              About
            </span>
          </CustomBreadcrumb>
          <SectionTitle level={1}>About Asvitloaten - OLS Master Teacher L4</SectionTitle>
          <FeatureCard>
            <BioSectionTitle level={3}>Nazar Pankiv, Known as Asvitloaten</BioSectionTitle>
            <BioText>
              <strong>OLS Master Teacher L4 | Founder & CEO of onelightsystem OLS</strong>
              <br />
              <strong>13.66 Light Days Old (19,608 Light Minutes as of 107.3405)</strong>
              <br />
              <br />
              Asvitloaten, also known as Nazar Pankiv, is the visionary founder of onelightsystem OLS, a global platform advancing Sun Light Meditation. Through{" "}
              <a href="https://www.olsme.com" target="_blank" rel="noopener noreferrer">
                olsme.com
              </a>
              , he guides students worldwide to build their Light bodies, fostering a Light Civilization rooted in spiritual innovation and environmental stewardship.
            </BioText>

            <BioSectionTitle level={3}>Early Life and Journey</BioSectionTitle>
            <BioText>
              Born on October 14, 1984, in Ternopil, Ukraine, Nazar graduated from Ternopil National Economic University (2001–2005). His activism during the 2004–05 Orange Revolution in Kyiv shaped his dedication to societal transformation. In 2005, he moved to Chicago, USA, studying ESL at Truman College while working as a cab driver. His spiritual path began in 2012 with certifications in Reiki and Theta Healing, followed by a pivotal move to Mount Shasta, California, in 2013, where he launched <code>nazar-kosmo.com</code>.
            </BioText>

            <BioSectionTitle level={3}>Founding onelightsystem OLS</BioSectionTitle>
            <BioText>
              In April 2017, Nazar established onelightsystem OLS, introducing a unique Sun Light Meditation system to harness Light energy daily. By 2018, he founded onelightsystem OLS LLC, launching a Meditation Academy that served over 70 students globally in its first year—free of charge. His innovative approach blends meditation, environmental advocacy, and open-source transparency.
            </BioText>

            <BioSectionTitle level={3}>Key Milestones</BioSectionTitle>
            <BioList>
              <li><strong>2024:</strong> Achieved 19,608 Light Minutes, developed a 10-year plan (2024–2034), and expanded OLS to Munich, Germany.</li>
              <li><strong>2023:</strong> Established an OLS branch in Egypt’s Red Sea region and visited Taiwan to share insights.</li>
              <li><strong>2022:</strong> Celebrated 5 years of OLS with 16,615 Light Minutes and updated the OLS Calendar to v4.2.</li>
              <li><strong>2021:</strong> Earned the title "Asvitloaten" with 15,000 Light Minutes, leading livestreams and policy recommendations.</li>
              <li><strong>2020:</strong> Launched the Public Enlightenment Series for broader outreach.</li>
              <li><strong>2019:</strong> Released OLSLM assets publicly and upgraded the OLS Data Center.</li>
              <li><strong>2018:</strong> Founded OLS LLC, introducing OLSM and OLSLM frameworks.</li>
              <li><strong>2017:</strong> Invented OLS, accumulating 9,779 Light Minutes in the first 200 days.</li>
            </BioList>

            <BioSectionTitle level={3}>Vision and Impact</BioSectionTitle>
            <BioText>
              As of March 26, 2025—2,887 days since founding OLS—Asvitloaten continues to lead as CEO and Master Teacher L4. His global influence spans Egypt, Taiwan, France, and Germany, rooted in his Ukrainian heritage. OLS remains an open-source platform, tracking student progress at{" "}
              <a href="https://www.olsme.com" target="_blank" rel="noopener noreferrer">
                olsme.com
              </a>{" "}
              to inspire a Light-based future.
            </BioText>
            <BioText>
              Join OLS at{" "}
              <a href="https://www.olsme.com/register" target="_blank" rel="noopener noreferrer">
                olsme.com/register
              </a>{" "}
              to start your Sun Light Meditation journey with Asvitloaten and build your Light body daily.
            </BioText>

            <BioSectionTitle level={3}>OLS Mission Under Asvitloaten</BioSectionTitle>
            <BioText>
              OLS, under Asvitloaten’s leadership, empowers students with:
              <BioList>
                <li><strong>Sun Light Meditation:</strong> Safe techniques to receive and retain Light energy.</li>
                <li><strong>OLSM:</strong> Collective meditation for group illumination.</li>
                <li><strong>OLSLM:</strong> Personal tools to track Light Minutes and spiritual growth.</li>
              </BioList>
              The academy integrates scientific research, counseling, and a custom OLS Calendar to align practitioners with Light energy cycles.
            </BioText>

            <CustomButton>
              <Link to="/register/olssregister">Join OLS Today</Link>
            </CustomButton>
          </FeatureCard>
        </ProfileContainer>
      </MainContent>

      <StyledFooter>
        <Footer />
      </StyledFooter>
    </FullHeightLayout>
  );
};

export default AboutAA;